import React, { Dispatch, SetStateAction } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Stack } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { Select } from "components/forms";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import dayjs from "dayjs";
import { useAccountSelector, useDateFormatSelect } from "store";
import { CurrencyType } from "store/types";
import toIsoMidday from "utils/to-iso-midday";

import { currenciesArray } from "../../../constants";
import { DateRange } from "../Date-range/DateRange";
import { MeasureData, MeasureDataErrors, useGroupsForm } from "./GroupsFormContext";

interface Props
  extends Pick<MeasureData, "currency" | "date" | "accounting_date" | "effective_range_from" | "effective_range_to"> {
  measureDataKeys: number[];
  firstFoundError?: MeasureDataErrors;
  isCurrencyMeasure: boolean;
  defaultCurrency: CurrencyType;
  setZeroElementData: Dispatch<
    SetStateAction<{
      currency: CurrencyType | null;
      date: string | null;
      accounting_date: string | null;
      effective_range_from: string | null;
      effective_range_to: string | null;
    }>
  >;
  zeroDataFrom: string | null;
  zeroDataTo: string | null;
}

export const SectionSelects: React.FC<Props> = ({
  measureDataKeys,
  currency,
  date,
  accounting_date,
  firstFoundError,
  isCurrencyMeasure,
  defaultCurrency,
  effective_range_from,
  effective_range_to,
  setZeroElementData,
  zeroDataFrom,
  zeroDataTo
}) => {
  const dateFormat = useDateFormatSelect();

  const isEffectiveRange: boolean = useAccountSelector()?.allow_effective_range || false;

  const useMeasureData: boolean = Boolean(measureDataKeys.length);

  const { updateManyMeasureData } = useGroupsForm();

  const showCurrencyWarning = isCurrencyMeasure && currency && currency !== defaultCurrency;

  const handleDateFieldsUpdate = (
    fields: Partial<Pick<MeasureData, "date" | "accounting_date" | "effective_range_from" | "effective_range_to">>
  ) => {
    if (useMeasureData) {
      return updateManyMeasureData(measureDataKeys, fields);
    }
    setZeroElementData((prev) => ({ ...prev, ...fields }));
  };

  return (
    <Stack sx={{ gap: "10px" }}>
      <Stack direction="row" gap="10px" pl="32px">
        <DesktopDatePicker
          format={dateFormat}
          views={["year", "month", "day"]}
          slotProps={{
            inputAdornment: { position: "start" },
            popper: {
              sx: {
                ".MuiPickersCalendarHeader-labelContainer": {
                  fontSize: "0.9rem"
                }
              }
            },
            textField: {
              sx: {
                width: "150px !important"
              },
              error: !!firstFoundError?.date,
              helperText: firstFoundError?.date
            }
          }}
          value={dayjs(date)}
          label="Effective Date"
          onChange={(date) => {
            if (date && date.isValid()) {
              const dateValue = date ? toIsoMidday(date?.toISOString()) : date;
              handleDateFieldsUpdate({ date: dateValue });
            }
          }}
        />
        <DesktopDatePicker
          format={dateFormat}
          views={["year", "month", "day"]}
          slotProps={{
            field: {
              clearable: true,
              onClear: () => handleDateFieldsUpdate({ accounting_date: null })
            },
            inputAdornment: { position: "start" },
            popper: {
              sx: {
                ".MuiPickersCalendarHeader-labelContainer": {
                  fontSize: "0.9rem"
                }
              }
            },
            textField: {
              sx: {
                width: "190px !important",
                ".MuiInputAdornment-root": {
                  marginLeft: "0 !important",
                  marginRight: "0 !important"
                }
              },
              error: !!firstFoundError?.accounting_date,
              helperText: firstFoundError?.accounting_date
            }
          }}
          value={accounting_date ? dayjs(accounting_date) : null}
          label="Accounting Date"
          onChange={(date) => {
            if (date && date.isValid()) {
              const dateValue = date ? toIsoMidday(date?.toISOString()) : date;
              handleDateFieldsUpdate({ accounting_date: dateValue });
            }
          }}
        />
        <Select
          SelectDisplayProps={{ style: { padding: "8.5px" } }}
          formProps={{
            size: "small",
            sx: {
              width: "80px",
              flexShrink: 0,
              ".MuiInputBase-root": {
                borderRadius: "8px"
              }
            }
          }}
          sx={{
            ".MuiSelect-select": {
              padding: "8.5px"
            }
          }}
          IconComponent={ExpandMoreIcon}
          disabled={!currency}
          items={currenciesArray}
          defaultValue={currency}
          variant="outlined"
          label="Currency"
          value={currency}
          onChange={(e) => updateManyMeasureData(measureDataKeys, { currency: e.target.value as CurrencyType })}
        />
        {showCurrencyWarning && (
          <MaterialTooltip isShow title="Selected currency is different from the deal currency">
            <WarningAmberIcon sx={{ fill: "#fdcb6e", outline: "none", mt: "10px" }} />
          </MaterialTooltip>
        )}
      </Stack>
      {isEffectiveRange && (
        <Stack sx={{ pl: "32px", pr: "8px" }}>
          <DateRange
            defaultDateFrom={useMeasureData ? effective_range_from : zeroDataFrom} //before investor selection we use zero data from and to
            defaultDateTo={useMeasureData ? effective_range_to : zeroDataTo}
            onClear={() =>
              updateManyMeasureData(measureDataKeys, { effective_range_from: null, effective_range_to: null })
            }
            onSubmit={({ from, to }) => {
              handleDateFieldsUpdate({ effective_range_from: from, effective_range_to: to });
            }}
          />
        </Stack>
      )}
    </Stack>
  );
};
